import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import { blogPosts } from '@/mocks/blogPosts';
import useSEO from '@/hooks/useSEO';

const categories = ['All', 'Shower Doors', 'Glass Railings', 'Mirrors', 'Maintenance'];
const categoriesEs = ['Todos', 'Puertas de Ducha', 'Barandales de Vidrio', 'Espejos', 'Mantenimiento'];

const BlogPage = () => {
  const { i18n } = useTranslation();
  const isEs = i18n.resolvedLanguage === 'es';

  useSEO({
    title: isEs ? 'Blog de Vidrio Las Vegas | Consejos, Costos e Ideas | Quinolux' : 'Glass & Mirror Blog Las Vegas | Tips, Costs & Ideas | Quinolux',
    description: isEs ? 'Consejos de expertos sobre puertas de ducha, barandales de vidrio, espejos y más para propietarios de Las Vegas.' : 'Expert tips on shower doors, glass railings, custom mirrors and more for Las Vegas homeowners. Cost guides, design ideas, and maintenance tips.',
    keywords: 'shower doors Las Vegas blog, glass railings Las Vegas tips, custom mirrors Las Vegas ideas, frameless shower door cost Las Vegas',
    canonical: 'https://quinoluxglass.com/blog',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: isEs ? 'Blog de Vidrio Las Vegas | Quinolux' : 'Glass & Mirror Blog Las Vegas | Quinolux',
      description: isEs ? 'Consejos de expertos sobre puertas de ducha, barandales de vidrio y espejos para propietarios de Las Vegas.' : 'Expert tips on shower doors, glass railings, and custom mirrors for Las Vegas homeowners.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/blog`,
      publisher: {
        '@type': 'Organization',
        name: 'Quinolux Glass & Mirrors',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}`,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/blog` },
        ],
      },
    },
  });

  const [activeCategory, setActiveCategory] = useState('All');
  const cats = isEs ? categoriesEs : categories;
  const enCats = categories;

  const filtered = activeCategory === 'All' || activeCategory === 'Todos'
    ? blogPosts
    : blogPosts.filter(p => p.category === enCats[cats.indexOf(activeCategory)]);

  const featured = blogPosts[0];
  const rest = filtered.filter(p => p.id !== featured.id);

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://public.readdy.ai/ai/img_res/c52d318a48360ea6469c9a3f9b6b38ef.jpg" alt="" className="w-full h-full object-cover object-top" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">
            {isEs ? 'Recursos y Consejos' : 'Resources & Tips'}
          </span>
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {isEs ? 'Blog de Vidrio' : 'Glass & Mirror Blog'}
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
            {isEs ? 'Consejos de expertos, guías de costos e ideas de diseño para propietarios de Las Vegas.' : 'Expert tips, cost guides, and design ideas for Las Vegas homeowners and contractors.'}
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link to={`/blog/${featured.slug}`} className="group block">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-gray-100 hover:border-gray-200 transition-colors duration-300">
              <div className="relative h-72 lg:h-auto min-h-[360px] overflow-hidden">
                <img src={featured.img} alt={isEs ? featured.titleEs : featured.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-5 left-5">
                  <span className="bg-black text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    {isEs ? 'Destacado' : 'Featured'}
                  </span>
                </div>
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center bg-[#F8F8F6]">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-white text-black text-xs font-semibold px-3 py-1 rounded-full border border-gray-200">
                    {isEs ? featured.categoryEs : featured.category}
                  </span>
                  <span className="text-gray-400 text-xs">{featured.date}</span>
                  <span className="text-gray-300 text-xs">·</span>
                  <span className="text-gray-400 text-xs">{featured.readTime}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-light text-black leading-tight mb-4 group-hover:text-gray-700 transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {isEs ? featured.titleEs : featured.title}
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  {isEs ? featured.excerptEs : featured.excerpt}
                </p>
                <div className="flex items-center gap-2 text-black text-sm font-semibold group-hover:gap-3 transition-all duration-300">
                  {isEs ? 'Leer Artículo' : 'Read Article'} <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* All Articles */}
      <section className="pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Category Filter */}
          <div className="flex items-center gap-2 mb-10 overflow-x-auto scrollbar-hide pb-1">
            {cats.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${activeCategory === cat ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map(post => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group block cursor-pointer">
                <div className="relative overflow-hidden rounded-2xl h-52 mb-4">
                  <img src={post.img} alt={isEs ? post.titleEs : post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 text-black text-xs font-semibold px-3 py-1 rounded-full">
                      {isEs ? post.categoryEs : post.category}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-gray-400 text-xs">{post.date}</span>
                  <span className="text-gray-300 text-xs">·</span>
                  <span className="text-gray-400 text-xs">{post.readTime}</span>
                </div>
                <h3 className="text-black font-semibold text-base leading-snug mb-2 group-hover:text-gray-700 transition-colors line-clamp-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {isEs ? post.titleEs : post.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-3">
                  {isEs ? post.excerptEs : post.excerpt}
                </p>
                <div className="flex items-center gap-2 text-black text-sm font-medium group-hover:gap-3 transition-all duration-300">
                  {isEs ? 'Leer Más' : 'Read More'} <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-light text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {isEs ? '¿Listo para su Proyecto?' : 'Ready to Start Your Project?'}
          </h2>
          <p className="text-gray-400 text-sm mb-8">
            {isEs ? 'Llame a Quinolux Glass & Mirrors para un estimado gratuito en Las Vegas.' : 'Call Quinolux Glass & Mirrors for a free estimate anywhere in Las Vegas.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+17026042093" className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill"></i> (702) 604-2093
            </a>
            <Link to="/free-quote" className="inline-flex items-center gap-2 border border-gray-600 text-white text-sm font-medium px-8 py-4 rounded-full hover:border-white transition-colors whitespace-nowrap cursor-pointer">
              {isEs ? 'Cotización Gratis' : 'Get Free Quote'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </main>
  );
};

export default BlogPage;
